/**
 * Personal Info Mock Data Panel
 *
 * 开发环境下的快捷填充工具
 * 提供可视化的面板用于快速填充个人信息数据
 *
 * @module mockDataGenerator.demo
 */
import { Button, Form, message } from "antd";
import { FormInstance } from "antd/lib/form";
import { useEffect, useState } from "react";
import {
  PersonalInfoFormData,
  generatePersonalInfoMockData,
  mockDataPresets,
} from "./mockDataGenerator.personal-info";
import AllCountryCodes from "../../data/country.json";
import { triggerFileUpload } from "@/utils/mockFileUploadTrigger";
import { delay, triggerReactInputChange } from "@/utils";

/**
 * React 内部的值跟踪器接口
 * 用于跟踪 input 元素的值变化
 */
interface ValueTracker {
  getValue(): string;
  setValue(value: string): void;
  stopTracking(): void;
}

/**
 * 扩展 HTMLInputElement 类型，添加 React 内部使用的 _valueTracker 属性
 */
declare global {
  interface HTMLInputElement {
    _valueTracker?: ValueTracker;
  }
}

interface PersonalInfoMockDataPanelProps {
  /** 表单实例 */
  form?: FormInstance;
}

/**
 * 个人信息 Mock 数据生成工具面板
 */
export function PersonalInfoMockDataPanel({
  form,
}: PersonalInfoMockDataPanelProps) {
  const [visible, setVisible] = useState(false);

  if (!form) {
    return null;
  }

  const idFrontOverseas = Form.useWatch("idFrontOverseas", form);

  useEffect(() => {
    if (idFrontOverseas && idFrontOverseas.length > 0) {
      (async () => {
        await delay(200);
        console.log("已设置正面证件照，设置背面证件照");
        // 上传完成后，将正面证件照设置为背面证件照
        form.setFieldValue("idBackOverseas", idFrontOverseas);
      })();
    }
  }, [idFrontOverseas, form]);

  /**
   * 触发文件上传控件
   * 模拟用户点击上传按钮
   */
  const handleTriggerFileUpload = async () => {
    message.loading({
      content: "正在准备文件...",
      key: "upload",
      duration: 0,
    });

    const success = await triggerFileUpload({
      fileUrl: "/icon.png",
      fileName: "id-frontend-overseas.png",
      onSuccess: (file) => {
        message.success({
          content: "✅ 已触发文件上传",
          key: "upload",
          duration: 2,
        });
        console.log("✅ 已触发文件上传:", file);
      },
      onError: (error) => {
        message.error({
          content: "❌ 触发文件上传失败",
          key: "upload",
          duration: 2,
        });
        console.error("❌ 触发文件上传失败:", error);
      },
    });

    if (!success) {
      message.error({
        content: "❌ 触发文件上传失败",
        key: "upload",
        duration: 2,
      });
    }
  };

  const handleTriggerSelectNationality = async (
    data: PersonalInfoFormData,
    message: string
  ) => {
    const selectButton = document.querySelector(
      'button[aria-label="请选择国籍/公民权"]'
    ) as HTMLInputElement;

    // 唤起国籍选择器
    if (selectButton) {
      console.log("🔧 模拟点击选择国籍/公民权");
      selectButton.click();
    }

    // 延迟300ms
    await delay(300);
    // 获取搜索输入框
    const searchInput = document.querySelector(
      ".generic-selector-modal-search input"
    );
    if (searchInput) {
      triggerReactInputChange(
        searchInput as HTMLInputElement,
        data.nationality
      );

      // 获取搜索输入框后，延迟200ms后，模拟点击选择国籍
      await delay(200);
      const selectNationality = AllCountryCodes.data.find(
        (country) => country.code2 === data.nationality
      );

      const nationalityElement = document.querySelector(
        `div[role="button"]#${selectNationality?.code2}-${selectNationality?.tel}`
      ) as HTMLInputElement;

      if (nationalityElement) {
        console.log(`🔧 模拟点击选择${nationalityElement}`);
        nationalityElement.click();
      }

      await delay(200);

      form.setFieldsValue(data);
      console.log(`✅ ${message}:`, data);

      await delay(200);
      if (message === "已填充海外居住中国公民") {
        handleTriggerFileUpload();
      }
    }
  };

  /* ============================================================
   * 预设场景操作
   * ========================================================== */

  const handleFillHkResident = () => {
    const data = mockDataPresets.hkResidentWithIdCard();
    handleTriggerSelectNationality(data, "已填充香港居民（身份证）");
  };

  // const handleFillMainlandChinese = () => {
  //   const data = mockDataPresets.mainlandChineseWithPassport();
  //   form.setFieldsValue(data);
  //   console.log("✅ 已填充中国大陆居民（护照）:", data);
  // };

  const handleFillOverseasChinese = () => {
    const data = mockDataPresets.overseasChinese();
    handleTriggerSelectNationality(data, "已填充海外居住中国公民");
  };

  const handleFillForeignResident = () => {
    const data = mockDataPresets.foreignResident();
    handleTriggerSelectNationality(data, "已填充外国居民（护照）");
  };

  const handleFillPermanentId = () => {
    const data = mockDataPresets.permanentIdDocument();
    handleTriggerSelectNationality(data, "已填充长期有效证件");
  };

  const handleFillRandom = () => {
    const data = generatePersonalInfoMockData();
    handleTriggerSelectNationality(data, "已填充随机个人数据");
  };

  const handleClear = () => {
    form.resetFields();
    console.log("✅ 已清空个人信息表单");
  };

  const handleToggle = () => {
    setVisible(!visible);
  };

  return (
    <>
      {/* 浮动按钮 */}
      {!visible && (
        <div
          className="fixed bottom-8 right-8 z-[9999] group cursor-pointer"
          onClick={handleToggle}
          style={{
            width: "64px",
            height: "64px",
          }}
        >
          <div
            className="absolute inset-0 rounded-full transition-all duration-300 group-hover:scale-110"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              boxShadow:
                "0 10px 25px -5px rgba(102, 126, 234, 0.5), 0 8px 10px -6px rgba(102, 126, 234, 0.3)",
            }}
          />
          <div
            className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            style={{
              background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
              boxShadow: "0 15px 35px -5px rgba(118, 75, 162, 0.6)",
            }}
          />
          <div className="relative w-full h-full flex items-center justify-center text-3xl transition-transform duration-300 group-hover:scale-110 group-active:scale-95">
            👤
          </div>
          <div
            className="absolute inset-0 rounded-full animate-ping opacity-20"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              animationDuration: "3s",
            }}
          />
        </div>
      )}

      {/* 主面板 */}
      {visible && (
        <div
          className="fixed bottom-8 right-8 z-[9999] bg-white rounded-lg shadow-2xl"
          style={{
            width: "320px",
            maxHeight: "600px",
            display: "flex",
            flexDirection: "column",
            border: "1px solid #d9d9d9",
          }}
        >
          {/* 标题栏 */}
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{
              borderBottom: "1px solid #f0f0f0",
              backgroundColor: "#fafafa",
              borderRadius: "8px 8px 0 0",
            }}
          >
            <span className="font-semibold text-base">
              👤 个人信息 Mock工具
            </span>
            <Button size="small" onClick={handleToggle}>
              ✕
            </Button>
          </div>

          {/* 内容区域 */}
          <div
            className="overflow-y-auto p-4 space-y-4"
            style={{ maxHeight: "500px" }}
          >
            {/* 预设场景 */}
            <div className="space-y-2">
              <div className="text-sm font-semibold text-gray-700">
                预设场景
              </div>
              <Button size="small" block onClick={handleFillHkResident}>
                香港居民（身份证）
              </Button>
              <Button size="small" block onClick={handleFillOverseasChinese}>
                海外居住中国公民
              </Button>
              <Button size="small" block onClick={handleFillForeignResident}>
                外国居民（护照）
              </Button>
              <Button size="small" block onClick={handleFillPermanentId}>
                长期有效证件
              </Button>
            </div>

            {/* 分割线 */}
            <div className="border-t border-gray-200" />

            {/* 其他操作 */}
            <div className="space-y-2">
              <div className="text-sm font-semibold text-gray-700">
                其他操作
              </div>
              <Button size="small" block onClick={handleFillRandom}>
                随机生成
              </Button>
            </div>
          </div>

          {/* 清空按钮 */}
          <div className="px-4 pb-4 pt-2 border-t">
            <Button danger size="small" block onClick={handleClear}>
              清空表单
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

export default PersonalInfoMockDataPanel;
